import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * DOM 実測ベースのページ自動分割フック。
 *
 * 各ページの scrollHeight と `maxHeightPx` の比から必要分割数を一度に計算し、
 * 一回の setPages で全ページを再分割する。これにより setState ループ上限
 * (React の Maximum update depth) に達することを避ける。
 *
 * - 分割後の sub-pages は元の page から派生するため、セクション境界は自動的に保たれる。
 * - 行数 1 のページはそれ以上縮められないので対象外。
 * - Jest (jsdom) では scrollHeight が常に 0 のためオーバーフロー検知が発動せず、
 *   初期ページ配列がそのまま使われる。
 */

// A4 縦 297mm から @page margin (上下 10mm 計 20mm) を引いた本体高さ。
// 297 - 10 - 10 = 277mm
const A4_BODY_HEIGHT_MM = 297 - 10 * 2;
export const DEFAULT_A4_BODY_HEIGHT_PX = Math.round((A4_BODY_HEIGHT_MM * 96) / 25.4); // 277mm → 約 1046px

const MAX_ITERATIONS = 10;

export type UsePageAutoSplitOptions<TPage, TRow> = {
  initialPages: TPage[];
  getRows: (page: TPage) => TRow[];
  replaceRows: (page: TPage, rows: TRow[]) => TPage;
  maxHeightPx?: number;
};

export type UsePageAutoSplitResult<TPage> = {
  pages: TPage[];
  pageRefs: MutableRefObject<Array<HTMLDivElement | null>>;
};

export function usePageAutoSplit<TPage, TRow>(
  options: UsePageAutoSplitOptions<TPage, TRow>,
): UsePageAutoSplitResult<TPage> {
  const { initialPages, getRows, replaceRows, maxHeightPx = DEFAULT_A4_BODY_HEIGHT_PX } = options;
  const [pages, setPages] = useState<TPage[]>(initialPages);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const iterationsRef = useRef(0);
  // 同じ rows 長構成で再計算しないようシグネチャでガード
  const lastSignatureRef = useRef<string>('');

  useEffect(() => {
    setPages(initialPages);
    iterationsRef.current = 0;
    lastSignatureRef.current = '';
  }, [initialPages]);

  useLayoutEffect(() => {
    if (iterationsRef.current >= MAX_ITERATIONS) {
      // eslint-disable-next-line no-console
      console.warn('usePageAutoSplit: 反復上限に達しました。scrollHeight と maxHeightPx の関係を確認してください。');
      return;
    }

    const signature = pages.map((p) => getRows(p).length).join(',');
    if (signature === lastSignatureRef.current) return;
    lastSignatureRef.current = signature;

    // 各ページの必要分割数 (rowsPerPage) を事前計算
    const splitPlan = new Map<number, number>();
    pages.forEach((page, i) => {
      const el = pageRefs.current[i];
      if (!el) return;
      const rows = getRows(page);
      if (rows.length <= 1) return;
      if (el.scrollHeight <= maxHeightPx) return;

      // scrollHeight / maxHeightPx 比から、必要なページ数を概算
      // 例: 45 行で scrollHeight=1500, maxHeightPx=1046 → ratio=1.43 → 2 ページ → 23行ずつ
      const ratio = el.scrollHeight / maxHeightPx;
      const neededPageCount = Math.max(2, Math.ceil(ratio));
      const rowsPerPage = Math.max(1, Math.ceil(rows.length / neededPageCount));
      if (rowsPerPage < rows.length) {
        splitPlan.set(i, rowsPerPage);
      }
    });

    if (splitPlan.size === 0) return;

    iterationsRef.current += 1;
    setPages((prev) => {
      const next: TPage[] = [];
      prev.forEach((page, i) => {
        const rowsPerPage = splitPlan.get(i);
        if (rowsPerPage === undefined) {
          next.push(page);
          return;
        }
        const rows = getRows(page);
        for (let j = 0; j < rows.length; j += rowsPerPage) {
          next.push(replaceRows(page, rows.slice(j, j + rowsPerPage)));
        }
      });
      return next;
    });
  }, [pages, getRows, replaceRows, maxHeightPx]);

  return { pages, pageRefs };
}
